/* eslint-disable max-statements */
import { Anchor, Grid, Stack, Title } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';

import { Declaration, DeclarationHighValueGroupType, EnvironmentCountries, TransactionTypes } from '@e-origin/shared';

import { EucdmCode, HideElementFor, MultiLangCodes, NumberInput, TextInput } from '@components';
import { useCodeListSystem } from '@hooks/useCodeListSystem';
import { useSettings } from '@hooks/useSettings';
import { disableAmendmentField } from '@utils';
import { BeforeSendingFormData } from './beforeSendingBatchFormIntializer';

interface FinalizeCustomsTreatmentProps {
  form: UseFormReturnType<BeforeSendingFormData>;
  declaration: Declaration;
  disabled: boolean;
}

export const FinalizeCustomsTreatment: React.FC<FinalizeCustomsTreatmentProps> = ({ form, declaration, disabled }) => {
  const isExport = declaration?.generalInfo?.isExport;
  const declarationGroup = declaration?.generalInfo?.group as DeclarationHighValueGroupType;
  const paymentDetailsDisabled = declaration.customer !== form.values.customer || disabled;

  const { data: settings } = useSettings();

  const isNetherlands = settings.country === EnvironmentCountries.NL;

  const codelistSystems = useCodeListSystem({
    group: declarationGroup,
    isExport,
  });

  const shouldHighlightCurrency = (value: number | string | undefined, currency: string | undefined): boolean => {
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    return !!(numValue && numValue > 0 && !currency);
  };

  const missingCurrencyProps = (value: number | string | undefined, currency: string | undefined) => ({
    error: shouldHighlightCurrency(value, currency) ? 'Currency is required when charges amount is specified' : undefined,
    styles: shouldHighlightCurrency(value, currency)
      ? {
          input: {
            borderColor: '#fa5252',
            '&:focus': {
              borderColor: '#fa5252',
            },
          },
        }
      : undefined,
  });

  const customsChargesValue = form.values.valuationInformationTaxes?.chargesimport?.CustomsCharges?.charges;
  const customsChargesCurrency = form.values.valuationInformationTaxes?.chargesimport?.CustomsCharges?.currency;
  const vatChargesValue = form.values.valuationInformationTaxes?.chargesimport?.VATcharges?.charges;
  const vatChargesCurrency = form.values.valuationInformationTaxes?.chargesimport?.VATcharges?.currency;

  if (!declaration) {
    return null;
  }
  return (
    <>
      <Stack>
        {!settings?.isForwarder && (
          <>
            <HideElementFor condition={() => declarationGroup === DeclarationHighValueGroupType.H2}>
              <Grid mb="sm">
                <Grid.Col span={6}>
                  <EucdmCode path="messageInfo.methodOfPayment">
                    <MultiLangCodes
                      {...form.getInputProps('messageInfo.methodOfPayment')}
                      label="Payment Method"
                      code="CL104"
                      disabled={disabled || disableAmendmentField({ declaration, settingsCountry: settings.country, countries: [EnvironmentCountries.BE] })}
                      size="lg"
                      data-pw="payment-method-input"
                      system={codelistSystems}
                    />
                  </EucdmCode>
                </Grid.Col>
                <Grid.Col span={6}>
                  <EucdmCode path="messageInfo.deferredPayment">
                    <TextInput
                      {...form.getInputProps('messageInfo.deferredPayment')}
                      label="Deferred payment"
                      size="lg"
                      disabled={
                        paymentDetailsDisabled ||
                        disableAmendmentField({ declaration, settingsCountry: settings.country, countries: [EnvironmentCountries.BE] })
                      }
                      data-pw="deferred-payment-input"
                    />
                  </EucdmCode>
                </Grid.Col>
              </Grid>
            </HideElementFor>
            <Grid mb="sm">
              <HideElementFor condition={() => declarationGroup === DeclarationHighValueGroupType.H2}>
                <Grid.Col span={6}>
                  <EucdmCode path="messageInfo.methodOfPayment">
                    <MultiLangCodes
                      {...form.getInputProps('messageInfo.methodOfPaymentVAT')}
                      label="VAT Payment Method"
                      code="CL104"
                      disabled={disabled || disableAmendmentField({ declaration, settingsCountry: settings.country, countries: [EnvironmentCountries.BE] })}
                      size="lg"
                      data-pw="vat-payment-method-input"
                      system={codelistSystems}
                    />
                  </EucdmCode>
                </Grid.Col>
              </HideElementFor>
              {isNetherlands && (
                <HideElementFor condition={() => declarationGroup === DeclarationHighValueGroupType.H2 && settings.country === EnvironmentCountries.NL}>
                  <Grid.Col span={6}>
                    <TextInput
                      {...form.getInputProps('messageInfo.deferredPaymentVAT')}
                      label="Deferred payment VAT"
                      size="lg"
                      disabled={paymentDetailsDisabled}
                      data-pw="vat-deferred-payment-input"
                    />
                  </Grid.Col>
                </HideElementFor>
              )}
            </Grid>
          </>
        )}
        <Grid>
          <HideElementFor condition={() => declarationGroup === DeclarationHighValueGroupType.H2}>
            <Grid.Col span={4}>
              <EucdmCode path="valuationInformationTaxes.valuationMethod">
                <MultiLangCodes
                  {...form.getInputProps('valuationInformationTaxes.valuationMethod')}
                  label="Valuation method"
                  code="CL604"
                  disabled={disabled}
                  size="lg"
                  data-pw="valuation-method-input"
                  system={codelistSystems}
                />
              </EucdmCode>
            </Grid.Col>
          </HideElementFor>
          <HideElementFor condition={() => declarationGroup === DeclarationHighValueGroupType.H2 && settings.country === EnvironmentCountries.NL}>
            {isNetherlands && (
              <Grid.Col span={4}>
                <EucdmCode path="valuationInformationTaxes.valuationAdjustment.valuationIndicators">
                  <TextInput
                    {...form.getInputProps('valuationInformationTaxes.valuationAdjustment.valuationIndicators')}
                    label="Valuation indicators"
                    size="lg"
                    data-pw="valuation-indicators-input"
                  />
                </EucdmCode>
              </Grid.Col>
            )}
            {isNetherlands && (
              <Grid.Col span={1}>
                <Anchor href={'/help'}>Valuation indicators description</Anchor>
              </Grid.Col>
            )}
          </HideElementFor>
        </Grid>
      </Stack>
      <Stack>
        <Title order={4}>Total</Title>
        <Grid mb="sm">
          <Grid.Col span={2.4}>
            <EucdmCode path="goodsShipmentItems.goodsMeasure.grossMass">
              <TextInput label="Total Weight" value={declaration.globalInfo?.totalWeight || ''} size="lg" disabled data-pw="total-weight-input" />
            </EucdmCode>
          </Grid.Col>
          <HideElementFor condition={() => declarationGroup === DeclarationHighValueGroupType.H2 && settings.country === EnvironmentCountries.NL}>
            <Grid.Col span={2.4}>
              <EucdmCode path="declarationGlobal.totalAmountInvoiced">
                <TextInput
                  label="Total amount invoiced"
                  value={declaration.globalInfo?.totalAmountInvoiced || ''}
                  size="lg"
                  disabled
                  data-pw="total-amount-invoiced-input"
                />
              </EucdmCode>
            </Grid.Col>
          </HideElementFor>
          <HideElementFor condition={() => declarationGroup === DeclarationHighValueGroupType.H2 && settings.country === EnvironmentCountries.NL}>
            <Grid.Col span={2.4}>
              <EucdmCode path="goodShipmentGlobal.invoiceCurrency">
                <TextInput label="Invoice currency" value={declaration.globalInfo?.invoiceCurrency || ''} size="lg" disabled data-pw="invoice-currency-input" />
              </EucdmCode>
            </Grid.Col>
          </HideElementFor>
          <Grid.Col span={2.4}>
            <TextInput label="Total PCS" value={declaration.globalInfo?.totalPCS || ''} size="lg" disabled data-pw="total-pcs-input" />
          </Grid.Col>
          <Grid.Col span={2.4}>
            <TextInput label="Total CTNS" value={declaration.globalInfo?.totalCNTS || ''} size="lg" disabled data-pw="total-cnts-input" />
          </Grid.Col>
        </Grid>
        {[TransactionTypes.B2B_HV, TransactionTypes.B2B_EXPORT, TransactionTypes.B2B2C_HV, undefined].includes(declaration.transactionType) && (
          <Grid mb="sm">
            <Grid.Col span={12}>
              <EucdmCode path="goodsShipmentItems.packaging.typeOfPackages">
                <MultiLangCodes
                  {...form.getInputProps('declarationGoods.packaging.typeOfPackages')}
                  label="Package Type"
                  code="CL017"
                  disabled={disabled}
                  size="lg"
                  data-pw="package-type-input"
                  system={codelistSystems}
                />
              </EucdmCode>
            </Grid.Col>
          </Grid>
        )}
      </Stack>
      {!isExport &&
        ![
          TransactionTypes.B2C_HV,
          TransactionTypes.B2C_HV_BEGATE,
          TransactionTypes.B2C_C2C_HV,
          TransactionTypes.B2C_EXPORT_MULTI,
          TransactionTypes.B2C_C2C_HV_H1_BUYER,
          TransactionTypes.B2B_MULTIPLE,
        ].includes(declaration.transactionType) && (
          <HideElementFor condition={() => declarationGroup === DeclarationHighValueGroupType.H2}>
            <Stack>
              <Title order={4}>Charges import</Title>
              <Grid mb="sm">
                <Grid.Col span={12}>
                  <EucdmCode path="valuationInformationTaxes.deliveryTerms.incotermCode">
                    <TextInput
                      label="Delivery terms"
                      value={declaration.valuationInformationTaxes.deliveryTerms.incotermCode}
                      size="lg"
                      disabled
                      data-pw="delivery-terms-input"
                    />
                  </EucdmCode>
                </Grid.Col>
              </Grid>
              <Grid mb="sm">
                <Grid.Col span={6}>
                  <EucdmCode path="valuationInformationTaxes.globalChargesimport.CustomsCharges.charges">
                    <NumberInput
                      {...form.getInputProps('valuationInformationTaxes.chargesimport.CustomsCharges.charges')}
                      label="Customs charges"
                      value={form.values.valuationInformationTaxes.chargesimport.CustomsCharges.charges}
                      size="lg"
                      disabled={disabled}
                      data-pw="customs-charges-input"
                    />
                  </EucdmCode>
                </Grid.Col>
                <Grid.Col span={6}>
                  <MultiLangCodes
                    data-pw="customs-charges-currency-input"
                    {...form.getInputProps('valuationInformationTaxes.chargesimport.CustomsCharges.currency')}
                    label="Currency"
                    code="Currency"
                    disabled={disabled}
                    size="lg"
                    {...missingCurrencyProps(customsChargesValue, customsChargesCurrency)}
                    system={codelistSystems}
                  />
                </Grid.Col>
              </Grid>

              <Grid mb="sm">
                <Grid.Col span={6}>
                  <EucdmCode path="valuationInformationTaxes.globalChargesimport.VATcharges.charges">
                    <NumberInput
                      {...form.getInputProps('valuationInformationTaxes.chargesimport.VATcharges.charges')}
                      label="VAT charges"
                      size="lg"
                      disabled={disabled}
                      data-pw="vat-charges-input"
                    />
                  </EucdmCode>
                </Grid.Col>
                <Grid.Col span={6}>
                  <MultiLangCodes
                    data-pw="vat-charges-currency-input"
                    {...form.getInputProps('valuationInformationTaxes.chargesimport.VATcharges.currency')}
                    label="Currency"
                    code="Currency"
                    disabled={disabled}
                    size="lg"
                    {...missingCurrencyProps(vatChargesValue, vatChargesCurrency)}
                    system={codelistSystems}
                  />
                </Grid.Col>
              </Grid>
            </Stack>
          </HideElementFor>
        )}
    </>
  );
};
